import React, { lazy, Component } from 'react';
import { motion } from 'framer-motion';
import { Bot, Database, Workflow, KeyRound, Mail, Building2 } from 'lucide-react';
import { SectionLabel, SectionHeading, SecondaryButton } from './ui/UI';
// Use jsDelivr's Simple Icons mirror — serves raw colored SVGs reliably.
// We apply a CSS filter to force white so they match the dark theme.
const simpleIcon = (slug: string) => `https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/${slug}.svg`;
type Integration = {
  name: string;
  logo?: string;
  Icon?: ComponentType<{
    className?: string;
  }>;
};
// Only use slugs known to exist in Simple Icons. For ambiguous brand names
// (Microsoft 365 / Entra ID / Google Workspace there are no clean slugs),
// fall back to a Lucide icon so the cell always renders.
const integrations: Integration[] = [{
  name: 'AWS',
  logo: simpleIcon('amazonwebservices')
}, {
  name: 'Azure',
  logo: simpleIcon('microsoftazure')
}, {
  name: 'Google Cloud',
  logo: simpleIcon('googlecloud')
}, {
  name: 'Microsoft 365',
  Icon: Building2
}, {
  name: 'Entra ID',
  Icon: KeyRound
}, {
  name: 'Google Workspace',
  Icon: Mail
}, {
  name: 'Okta',
  logo: simpleIcon('okta')
}, {
  name: 'GitHub',
  logo: simpleIcon('github')
}, {
  name: 'GitLab',
  logo: simpleIcon('gitlab')
}, {
  name: 'Jira',
  logo: simpleIcon('jira')
}, {
  name: 'Slack',
  logo: simpleIcon('slack')
}, {
  name: 'ServiceNow',
  logo: simpleIcon('servicenow')
}, {
  name: 'Cloudflare',
  logo: simpleIcon('cloudflare')
}, {
  name: 'Kubernetes',
  logo: simpleIcon('kubernetes')
}, {
  name: 'AI app endpoints',
  Icon: Bot
}, {
  name: 'RAG systems',
  Icon: Database
}, {
  name: 'Agent toolchains',
  Icon: Workflow
}];
export function Integrations() {
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-959">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-960">
        <SectionLabel number="14" label="Integrations" className="mb-8" data-id="element-961" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12" data-id="element-962">
          <div className="lg:col-span-7" data-id="element-963">
            <SectionHeading size="xl" data-id="element-964">
              Built to connect
              <br data-id="element-965" />
              <span className="text-white/40" data-id="element-966">where risk lives.</span>
            </SectionHeading>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-967">
            <p className="text-lg text-white/60 leading-relaxed mb-4" data-id="element-968">
              Periscan is designed to validate across modern environments. Early
              access integrations will roll out in phases.
            </p>
            <p className="font-mono text-xs text-white/40 leading-relaxed uppercase tracking-widest" data-id="element-969">
              Cloud platforms, Identity providers, SaaS applications, Code
              repositories, AI applications, External domains, Internal
              networks, Ticketing systems, Security tools, Compliance workflows,
              MSSP reporting stacks.
            </p>
          </div>
        </div>

        {/* Integration grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/5 mb-12" data-id="element-970">
          {integrations.map((int, i) => {
          const Icon = int.Icon;
          return <motion.div key={i} initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: i * 0.02
          }} className="bg-[#070a0e] h-28 flex flex-col items-center justify-center gap-2.5 px-4 py-3 hover:bg-[#0a0d12] transition-colors group" data-id="element-971">
                <div className="h-7 flex items-center justify-center" data-id="element-972">
                  {int.logo ? <img src={int.logo} alt={`${int.name} logo`} loading="lazy" className="h-6 w-auto max-w-[7rem] opacity-70 group-hover:opacity-100 transition-opacity" style={{
                filter: 'brightness(0) invert(1)'
              }} onError={e => {
                // Hide a broken image so the label below still reads cleanly
                ;
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }} data-id="element-973" /> : Icon ? <Icon className="w-6 h-6 text-white/60 group-hover:text-white transition-colors" data-id="element-974" /> : null}
                </div>
                <span className="text-xs text-white/45 group-hover:text-white/80 transition-colors font-medium text-center leading-tight" data-id="element-975">
                  {int.name}
                </span>
              </motion.div>;
        })}
        </div>

        <SecondaryButton href="#early-access" size="md" data-id="element-976">
          Request Integration Access
        </SecondaryButton>
      </div>
    </section>;
}